import { clamp, wrap } from "../math";
import type { ApproachReferenceData, Plan, PlannerConfig } from "../types";
import type { CubeBelief } from "./belief";
import type { Clips } from "./clips";

/** Guarded v9.2 approach admission and post-walk visual progress checks. */
export class ApproachController {
  private attempts = 0;
  private noProgress = 0;
  private rung = -1;
  private awaitingMeasurement = false;
  private before = 0;
  private lastWindow = -1;
  private measureAnchor: Plan | null = null;
  private measureTargetYaw = 0;
  private turnAnchor: Plan | null = null;
  private turnAttempts = 0;
  private turnRung = -1;
  private turnTargetYaw = 0;

  constructor(
    private readonly cfg: PlannerConfig,
    private readonly source?: ApproachReferenceData,
  ) {
    if (cfg.approach_enabled && !source) {
      throw new Error("approach is enabled but its walk reference is missing");
    }
  }

  reset(): void {
    this.attempts = 0;
    this.noProgress = 0;
    this.rung = -1;
    this.awaitingMeasurement = false;
    this.before = 0;
    this.lastWindow = -1;
    this.measureAnchor = null;
    this.measureTargetYaw = 0;
    this.clearTurn();
  }

  shape(rawCandidate: Plan, belief: CubeBelief, robotYaw: number, clips: Clips): Plan {
    let raw = rawCandidate;
    if (this.turnAnchor) {
      if (clips.solved || clips.rungIndex !== this.turnRung) {
        this.clearTurn();
      } else if (raw.mode === "clip" && belief.pose) {
        raw = clips.retargetHeadingLocked(
          this.turnAnchor,
          belief.pose,
          robotYaw,
          this.turnTargetYaw,
        );
      }
    }
    this.updateFeedback(raw, belief, robotYaw, clips);
    return this.admit(raw, clips);
  }

  commit(plan: Plan, robotYaw: number, clips: Clips, matchedTargetYaw?: number): void {
    if (plan.approachTurn) {
      if (!this.turnAnchor) {
        this.turnAnchor = { ...plan, mode: "clip" };
        this.turnRung = clips.rungIndex;
        this.turnTargetYaw = wrap(robotYaw + plan.entryYaw);
      }
      this.turnAttempts += 1;
      return;
    }
    if (plan.mode === "approach") {
      this.clearTurn();
      this.before = plan.entryTranslation;
      this.measureAnchor = plan;
      this.measureTargetYaw = matchedTargetYaw ?? wrap(robotYaw + plan.matchedEntryYaw);
      this.awaitingMeasurement = true;
      this.rung = clips.rungIndex;
      this.lastWindow = plan.approachWindow;
      this.attempts += 1;
      return;
    }
    if (plan.mode === "clip") this.reset();
  }

  terminal(plan: Plan): boolean {
    return plan.mode === "settle" && [
      "approach blocked",
      "approach turn blocked",
      "reposition required",
    ].includes(plan.label);
  }

  private clearTurn(): void {
    this.turnAnchor = null;
    this.turnAttempts = 0;
    this.turnRung = -1;
    this.turnTargetYaw = 0;
  }

  private ready(candidate: Plan): boolean {
    return Math.abs(candidate.entryForward) <= (this.cfg.approach_enter_forward_m ?? 0.30)
      && Math.abs(candidate.entryLateral) <= (this.cfg.approach_enter_lateral_m ?? 0.10);
  }

  private updateFeedback(raw: Plan, belief: CubeBelief, robotYaw: number, clips: Clips): void {
    if (raw.mode !== "clip" || !belief.pose) return;
    if (this.rung >= 0 && clips.rungIndex !== this.rung) this.reset();
    if (this.awaitingMeasurement && this.measureAnchor) {
      const measured = clips.retargetHeadingLocked(
        this.measureAnchor,
        belief.pose,
        robotYaw,
        this.measureTargetYaw,
      );
      const progress = this.before - measured.entryTranslation;
      this.awaitingMeasurement = false;
      this.noProgress = progress + 1e-6 < (this.cfg.approach_min_progress_m ?? 0.05)
        ? this.noProgress + 1
        : 0;
    }
    if (this.ready(raw)) {
      this.attempts = 0;
      this.noProgress = 0;
      this.rung = clips.rungIndex;
      this.lastWindow = -1;
    }
  }

  private parked(raw: Plan, clips: Clips, label: string): Plan {
    return {
      ...clips.still(0, label),
      cost: raw.cost,
      entryTranslation: raw.entryTranslation,
      entryBearing: raw.entryBearing,
      entryForward: raw.entryForward,
      entryLateral: raw.entryLateral,
    };
  }

  private blocked(raw: Plan, clips: Clips, label: string): Plan {
    return this.cfg.approach_fallback_to_clip ? raw : this.parked(raw, clips, label);
  }

  private admit(raw: Plan, clips: Clips): Plan {
    if (raw.mode !== "clip" || !this.cfg.approach_enabled) return raw;
    if (this.cfg.approach_before_first_clip_only && clips.committedClips > 0) return raw;
    if (this.ready(raw)) return raw;
    if (this.attempts >= (this.cfg.approach_max_attempts ?? 2)
        || this.noProgress >= (this.cfg.approach_no_progress_limit ?? 2)) {
      return this.blocked(raw, clips, "approach blocked");
    }
    if (this.cfg.approach_forward_only && raw.entryForward <= 0) {
      return this.blocked(raw, clips, "reposition required");
    }
    const turnMax = (this.cfg.approach_turn_max_deg ?? 20) * Math.PI / 180;
    if (Math.abs(raw.entryBearing) > turnMax) {
      if (this.turnAttempts >= (this.cfg.approach_turn_max_attempts ?? 3)) {
        return this.blocked(raw, clips, "approach turn blocked");
      }
      const sweep = this.cfg.scan_sweep_deg * Math.PI / 180;
      return {
        ...clips.still(clamp(raw.entryBearing, -sweep, sweep), "turn->approach"),
        cost: raw.cost,
        row: raw.row,
        sym: raw.sym,
        delta: raw.delta,
        entryYaw: raw.entryYaw,
        matchedEntryYaw: raw.entryYaw,
        entryTranslation: raw.entryTranslation,
        entryBearing: raw.entryBearing,
        entryForward: raw.entryForward,
        entryLateral: raw.entryLateral,
        approachTurn: true,
      };
    }

    const source = this.source;
    if (!source || source.manifest.windows.length === 0) {
      return this.parked(raw, clips, "approach unavailable");
    }
    const requested = clamp(
      raw.entryForward - (this.cfg.approach_target_forward_m ?? 0.08),
      0,
      this.cfg.approach_max_step_m ?? 0.30,
    );
    const minimum = this.cfg.approach_escalate_window && this.noProgress > 0 && this.lastWindow >= 0
      ? Math.min(this.lastWindow + 1, source.manifest.windows.length - 1)
      : 0;
    let best = minimum;
    let bestError = Infinity;
    for (let index = minimum; index < source.manifest.windows.length; index += 1) {
      const error = Math.abs((source.manifest.windows[index]?.distance ?? 0) - requested);
      if (error < bestError) {
        bestError = error;
        best = index;
      }
    }
    const window = source.manifest.windows[best];
    if (!window) return this.parked(raw, clips, "approach unavailable");
    return {
      ...raw,
      mode: "approach",
      clip: -1,
      entry: 0,
      exit: window.end,
      hold: 0,
      label: `approach ${window.distance.toFixed(2)}m`,
      matchedEntryYaw: raw.entryYaw,
      entryYaw: wrap(raw.entryBearing - window.travel_from_root),
      approachWindow: best,
      approachRequested: requested,
      approachCovered: window.distance,
    };
  }
}
